// 이용약관 판(版) 목록·시각 판정 — 순수 함수만(import 없음). TermsOfService.js 가 판 전환 버튼·공고 상자·버전 상자·이력을 이 목록을 순회해 그린다.
//   utils/features.js 가 v8.0 시각 읽기 함수를 여기서 가져가 재수출하고, config/termsV80.js 가 날짜 포맷을 가져간다(둘 다 firebase 를 끌지 않으므로 node 검증 스크립트에서 바로 import 가능).
//
// v8.0 (2026-10-24 법적보안 초안, 공고 전): 공고일·시행일 모두 config/features 값(termsV80NoticeAt·termsV80At)으로만 정해진다 — 기본값 없음(= 미정).
//   둘 중 하나라도 없거나 형식이 틀리면 v8.0 은 「미정」 = 어디에도 그리지 않는다(fail-closed). 형식 오류는 console.error.
//   공고일 ≤ 지금 < 시행일 → 「개정 예정 v8.0」 상자·버튼·띠 / 시행 후 → v8.0 현행.
// v7.1 대체: v8.0 공고가 v7.1 시행(11-24) 전에 시작되면 v7.1 은 시행되지 않고 v8.0 공고로 대체된다(초안 §1 머리 「v7.1 공고 처리」 ⓐ). 그때 v7.1 은 「공고 당시 문안」으로만 열람.
//   v8.0 공고가 v7.1 시행 뒤면(ⓑ) v7.1 은 시행 중인 판이고 v8.0 은 그것을 개정하는 판이다.

// config/features 의 시각 값(ISO 문자열 · ms 숫자 · Firestore Timestamp)을 ms 로. 값이 없으면 null, 형식 오류면 console.error 후 null.
export function parseFeatureAtMs(v, key) {
    if (v === undefined || v === null || v === '') return null;
    let ms = NaN;
    if (typeof v === 'number') ms = v;
    else if (typeof v === 'string') ms = Date.parse(v);
    else if (v && typeof v.toMillis === 'function') ms = v.toMillis();
    else if (v && typeof v.seconds === 'number') ms = v.seconds * 1000;
    if (!Number.isFinite(ms)) {
        console.error(`[features] ${key} 형식 오류 — 미정으로 취급:`, v);
        return null;
    }
    return ms;
}

// 「2026년 11월 24일」 (KST). ms 가 null 이면 null.
export function formatKstDateKo(ms) {
    if (ms === null || ms === undefined || !Number.isFinite(ms)) return null;
    return new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: 'long', day: 'numeric' }).format(ms);
}
// 「2026-11-24」 (KST) — UI 라벨용(로그인 접힌 한 줄 제목). ms 가 null 이면 null.
export function formatKstDateIso(ms) {
    if (ms === null || ms === undefined || !Number.isFinite(ms)) return null;
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(ms);
}

// ── v8.0 시각 (features.js 가 재수출) ──
export function getTermsV80NoticeAtMs(features) {
    return parseFeatureAtMs(features?.termsV80NoticeAt, 'termsV80NoticeAt');
}
export function getTermsV80AtMs(features) {
    return parseFeatureAtMs(features?.termsV80At, 'termsV80At');
}
// 공고 시작 = 공고일·시행일 둘 다 있고 지금 ≥ 공고일. 하나만 있으면 미정(공고 전)으로 본다.
export function isTermsV80Announced(features, now = Date.now()) {
    const n = getTermsV80NoticeAtMs(features);
    const a = getTermsV80AtMs(features);
    return n !== null && a !== null && now >= n;
}
export function isTermsV80Effective(features, now = Date.now()) {
    return isTermsV80Announced(features, now) && now >= getTermsV80AtMs(features);
}
// 공고 띠 노출 기간: 공고일 ~ 시행 시각 전까지
export function isTermsV80NoticePeriod(features, now = Date.now()) {
    return isTermsV80Announced(features, now) && now < getTermsV80AtMs(features);
}

// ── 판 목록 ──
// ctx = { nowMs, v70NoticeAtMs, v70AtMs, v71NoticeAtMs, v71AtMs, v80NoticeAtMs|null, v80AtMs|null }
// 반환 각 판: { id, label, announced, effective, replaced, noticeAtMs, atMs, noticeDateKo, effectiveDateKo }
//   announced = 공고 시작(상자·버튼·조문을 그릴 수 있음) / effective = 시행 중(현행 또는 종전) / replaced = v7.1 이 v8.0 공고로 대체되어 시행되지 않음
export function buildTermsVersions(ctx) {
    const now = ctx.nowMs;
    const v80Announced = ctx.v80NoticeAtMs !== null && ctx.v80NoticeAtMs !== undefined
        && ctx.v80AtMs !== null && ctx.v80AtMs !== undefined && now >= ctx.v80NoticeAtMs;
    const v80Effective = v80Announced && now >= ctx.v80AtMs;
    // ⓐ: v8.0 공고일이 v7.1 시행 시각보다 앞 → v7.1 대체(시행되지 않음)
    const v71Replaced = v80Announced && ctx.v80NoticeAtMs < ctx.v71AtMs;
    const v71Announced = now >= ctx.v71NoticeAtMs;
    const v70Announced = now >= ctx.v70NoticeAtMs;
    const list = [
        { id: 'v62', label: 'v6.2', announced: true, effective: true, replaced: false, noticeAtMs: null, atMs: null },
        { id: 'v70', label: 'v7.0', announced: v70Announced, effective: now >= ctx.v70AtMs, replaced: false, noticeAtMs: ctx.v70NoticeAtMs, atMs: ctx.v70AtMs },
        { id: 'v71', label: 'v7.1', announced: v71Announced, effective: v71Announced && !v71Replaced && now >= ctx.v71AtMs, replaced: v71Replaced, noticeAtMs: ctx.v71NoticeAtMs, atMs: ctx.v71AtMs },
        { id: 'v80', label: 'v8.0', announced: v80Announced, effective: v80Effective, replaced: false, noticeAtMs: v80Announced ? ctx.v80NoticeAtMs : null, atMs: v80Announced ? ctx.v80AtMs : null },
    ];
    return list.map(v => ({
        ...v,
        noticeDateKo: formatKstDateKo(v.noticeAtMs),
        effectiveDateKo: formatKstDateKo(v.atMs),
    }));
}

// 현행 판 = 목록에서 시행 중인 마지막 판
export function currentTermsVersion(versions) {
    for (let i = versions.length - 1; i >= 0; i--) if (versions[i].effective) return versions[i];
    return versions[0];
}
// 어떤 판 뒤에 시행 중인 다음 판(종전 표기용). 없으면 null
export function nextEffectiveAfter(versions, id) {
    const i = versions.findIndex(v => v.id === id);
    for (let j = i + 1; j < versions.length; j++) if (versions[j].effective) return versions[j];
    return null;
}
// 판 전환 버튼 라벨 — 기존 세 판의 라벨 규칙을 일반화(v6.2: 종전/현행, v7.0: 종전/현행/개정 예정, v7.1: 현행/개정 예정). v7.1 대체 시 「공고 문안 보기」.
export function versionButtonLabel(versions, v) {
    if (v.replaced) return `${v.label} 공고 문안 보기`;
    const cur = currentTermsVersion(versions);
    if (v.effective) return cur.id === v.id ? `현행 약관 ${v.label} 보기` : `종전 약관 ${v.label} 보기`;
    return `개정 예정 약관 ${v.label} 보기`;
}
// 버전 상자 「버전:」 줄 — 기존 규칙 일반화: 종전 → 「vX (종전 — {다음 판 시행일}부터 {다음 판} 적용)」, 현행 → 「vX」, 예정 → 「vX ({시행일} 시행 예정 — 현행 {현행 판})」
export function versionLine(versions, v) {
    if (v.replaced) return `${v.label} (공고 당시 문안 — v8.0 공고로 대체되어 시행되지 않습니다)`;   // ☆ 자체 문구
    const cur = currentTermsVersion(versions);
    if (v.effective) {
        if (cur.id === v.id) return v.label;
        const next = nextEffectiveAfter(versions, v.id);
        return `${v.label} (종전 — ${next.effectiveDateKo}부터 ${next.label} 적용)`;
    }
    return `${v.label} (${v.effectiveDateKo} 시행 예정 — 현행 ${cur.label})`;
}
